import React, { useEffect } from 'react';
import { ActivityIndicator, Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { PRO_PRICE_USD, PRO_SUBSCRIPTION_META } from '../../services/billingService';
import { telemetry } from '../../services/telemetry';
import type { PaywallSource } from '../../services/telemetryTypes';
import { GlassButton } from '../ui/GlassButton';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { SubscriptionLegalLinks } from './SubscriptionLegalLinks';

export type AnalyzeQuotaInfo = {
  title?: string;
  message: string;
  upgradeRequired?: boolean;
  monthlyLimitReached?: boolean;
  remaining?: number;
  limit?: number;
  /** ISO date when Pro quota resets (billing anniversary). */
  periodResetsAt?: string;
};

type Props = {
  visible: boolean;
  info: AnalyzeQuotaInfo | null;
  onDismiss: () => void;
  /** Stripe Checkout — free tier exhausted. */
  onUpgrade?: () => void;
  upgradeBusy?: boolean;
  /** Which gate opened this sheet — used for the Pro conversion funnel. */
  source?: PaywallSource;
};

const PRO_VALUE_PROPS = [
  '200 AI analyses per billing period',
  'AI chat on spend, wealth, grow, and loan',
  'Household up to 50',
  'Spend alerts you set',
] as const;

function formatResetLabel(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

/**
 * Shown when the user taps Upload & analyze (or portfolio extract) and
 * free lifetime / Pro billing-period AI analyze quota is exhausted.
 */
export function AnalyzeQuotaModal({
  visible,
  info,
  onDismiss,
  onUpgrade,
  upgradeBusy = false,
  source = 'unknown',
}: Props) {
  const isProCap = info != null && info.monthlyLimitReached === true && info.upgradeRequired !== true;
  const showUpgrade =
    info != null && !isProCap && (info.upgradeRequired !== false || !!onUpgrade);

  useEffect(() => {
    if (!visible || !info || !showUpgrade) return;
    void telemetry.logPaywallView(source);
  }, [visible, info, showUpgrade, source]);

  if (!info) return null;

  const title = info.title ?? (isProCap ? 'Billing period limit reached' : 'Upgrade to Pro');
  const resetLabel = formatResetLabel(info.periodResetsAt);

  const handleDismiss = () => {
    if (showUpgrade) {
      void telemetry.logPaywallDismiss(source, 'not_now');
    }
    onDismiss();
  };

  const handleUpgrade = () => {
    if (!onUpgrade || upgradeBusy) return;
    void telemetry.logUpgradeCtaClick(source);
    onUpgrade();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleDismiss}>
      <View style={styles.overlay}>
        <Pressable style={styles.backdrop} onPress={handleDismiss} accessibilityLabel="Dismiss" />
        <View style={styles.card} accessibilityRole="alert">
          <Text style={styles.kicker}>{showUpgrade ? 'Pro' : 'Quota'}</Text>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.body}>{info.message}</Text>
          {showUpgrade ? (
            <View style={styles.valueList}>
              {PRO_VALUE_PROPS.map((line) => (
                <Text key={line} style={styles.valueItem}>
                  {line}
                </Text>
              ))}
              <Text style={styles.price}>{PRO_SUBSCRIPTION_META}</Text>
            </View>
          ) : null}
          {typeof info.limit === 'number' && !showUpgrade && !isProCap ? (
            <Text style={styles.meta}>{info.limit} free AI analyses included</Text>
          ) : null}
          {isProCap && resetLabel ? (
            <Text style={styles.reset}>
              Resets on {resetLabel} — your next subscription day.
            </Text>
          ) : null}
          <View style={styles.actions}>
            {showUpgrade ? (
              <GlassButton
                variant="primary"
                fullWidth
                disabled={upgradeBusy || !onUpgrade}
                onPress={handleUpgrade}
              >
                {upgradeBusy ? (
                  <ActivityIndicator color={colors.text} />
                ) : (
                  `Upgrade · $${PRO_PRICE_USD}/mo`
                )}
              </GlassButton>
            ) : null}
            {isProCap ? (
              <GlassButton variant="primary" fullWidth onPress={onDismiss}>
                {resetLabel ? `Resets ${resetLabel}` : 'Got it'}
              </GlassButton>
            ) : (
              <GlassButton
                variant={showUpgrade ? 'ghost' : 'primary'}
                fullWidth
                disabled={upgradeBusy}
                onPress={handleDismiss}
              >
                {showUpgrade ? 'Not now' : 'Got it'}
              </GlassButton>
            )}
          </View>
          {showUpgrade ? <SubscriptionLegalLinks onBeforeOpenLegal={onDismiss} /> : null}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(2, 6, 23, 0.55)',
  },
  card: {
    zIndex: 2,
    width: '100%',
    maxWidth: 400,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: colors.surface,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  kicker: {
    ...typography.caption,
    color: colors.accent,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  title: {
    ...typography.title,
    fontSize: 22,
    color: colors.text,
  },
  body: {
    ...typography.body,
    color: colors.textMuted,
    marginTop: spacing.xs,
  },
  valueList: {
    marginTop: spacing.sm,
    gap: 6,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(61, 214, 164, 0.06)',
  },
  valueItem: {
    ...typography.body,
    fontSize: 14,
    color: colors.text,
  },
  price: {
    ...typography.caption,
    color: colors.accent,
    marginTop: spacing.xs,
    fontWeight: '600',
  },
  meta: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: spacing.xs,
  },
  reset: {
    ...typography.body,
    color: colors.accent,
    marginTop: spacing.xs,
    fontWeight: '600',
  },
  actions: {
    marginTop: spacing.md,
    gap: spacing.sm,
  },
});
